import type { CustomerOrderResponse } from '~/rpcMethods'
import { formatPrescription } from '~/utils/rx'

const THRESHOLD_CONVERTIBLE_PROGRESSIVE_GLASSES = {
  sphere: {
    max: 6,
    min: -6,
  },
  cylinder: {
    max: 4,
    min: -4,
  },
  pd: {
    max: 40,
    min: 25,
  },
} as const

export const isProgressiveGlassesCheck = (
  prescription: CustomerOrderResponse,
) =>
  (prescription.left.addition || 0) !== 0 &&
  (prescription.right.addition || 0) !== 0

const isValueWithinThreshold = ({
  type,
  value,
}: {
  type: keyof typeof THRESHOLD_CONVERTIBLE_PROGRESSIVE_GLASSES
  value: number
}): boolean => {
  const { min, max } = THRESHOLD_CONVERTIBLE_PROGRESSIVE_GLASSES[type]
  return value <= max && value >= min
}

const isValidSide = (side: CustomerOrderResponse['left' | 'right']) =>
  !side.prism &&
  !side.base &&
  !side.pr &&
  isValueWithinThreshold({
    type: 'sphere',
    value: side.sphere + (side.addition || 0),
  })

export const isConvertibleProgressiveGlasses = (
  prescription: CustomerOrderResponse,
) => {
  if (!isProgressiveGlassesCheck(prescription)) return false

  const { left, right } = prescription
  return isValidSide(left) && isValidSide(right)
}

export const convertProgressiveGlasses = ({
  prescription,
  to,
}: {
  prescription: CustomerOrderResponse
  to: RxProgressiveGlassesPrescriptionConversionType
}): RxPrescriptionValues => {
  if (!isConvertibleProgressiveGlasses(prescription))
    throw new Error(
      'This is not an eligible pair of progressive glasses for conversion',
    )

  return to === 'distance'
    ? formatPrescription(prescription)
    : formatPrescription({
        left: {
          ...prescription.left,
          sphere: prescription.left.sphere + (prescription.left.addition || 0),
        },
        right: {
          ...prescription.right,
          sphere:
            prescription.right.sphere + (prescription.right.addition || 0),
        },
      })
}

export const handleInitialProgressiveGlassesPrescription = (
  prescription: CustomerOrderResponse,
): RxPrescriptionValues[] =>
  isProgressiveGlassesCheck(prescription)
    ? [
        {
          type: 'progressive',
          ...formatPrescription(prescription),
        },
      ]
    : []
